/**
 * Ender dragon renderer: vanilla DragonModel geometry (head with jaw and lips,
 * 10px neck / tail cubes, 24x24x64 body, two-piece wings with membranes) built
 * with `buildPart`, posed from the simulation's own chains so the neck and tail
 * follow the hitboxes exactly. Drawn at 2x vanilla scale (the hitboxes are
 * bigger than the 1x model). Skin and glowing eyes are the real
 * vanilla sheets (entity/enderdragon/dragon.png, dragon_eyes.png). Also draws crystals + beams, dragon fireballs, breath clouds and the
 * death beam.
 *
 *   const r = new DragonRenderer(env); scene.add(r.group);
 *   r.update(fight, alpha, camera, tick);   // every frame while in the End
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { DragonFight } from '../../entities/end/fight';
import { DEATH_TICKS, type ChainLink, type EnderDragon } from '../../entities/end/dragon';
import { buildPart, entityMaterial, setEffects, sheetTexture, type CubeDef, type EntityMat } from './core';
import { itemSpriteMesh } from './extras';
import { BeamPool, CrystalModel } from './dragonCrystal';

const S = 2; // model scale over vanilla
const PX = S / 16;

const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): CubeDef =>
  ({ tex, from, size, inflate, mirror });

/** Holder with vanilla model space (px, y down, front -z) scaled to blocks, yawed / pitched in the world. */
function holder(): { wrap: THREE.Group; inner: THREE.Group } {
  const wrap = new THREE.Group();
  wrap.rotation.order = 'YXZ';
  const inner = new THREE.Group();
  inner.scale.set(-PX, -PX, PX);
  wrap.add(inner);
  return { wrap, inner };
}

class DragonModel {
  readonly root = new THREE.Group();
  readonly mats: EntityMat[] = [];
  private readonly skin: EntityMat;
  private readonly body = holder();
  private readonly head = holder();
  private readonly jaw: THREE.Group;
  private readonly wings: Array<{ root: THREE.Group; tip: THREE.Group; side: number }> = [];
  private readonly legs: Array<{ root: THREE.Group; shin: THREE.Group; rear: boolean }> = [];
  private readonly neck: Array<ReturnType<typeof holder>> = [];
  private readonly tail: Array<ReturnType<typeof holder>> = [];

  constructor(env: EnvUniforms) {
    this.skin = entityMaterial(env, sheetTexture('entity/enderdragon/dragon'), { doubleSided: true });
    const eye = entityMaterial(env, sheetTexture('entity/enderdragon/dragon_eyes'), { emissive: true });
    this.mats.push(this.skin, eye);
    const T = 256;

    // body: origin at the model's own origin, centred on the hitbox by the offset below
    const bodyG = buildPart({
      cubes: [
        cube([0, 0], [-12, 0, -16], [24, 24, 64]),
        cube([220, 53], [-1, -6, -10], [2, 6, 12]),
        cube([220, 53], [-1, -6, 10], [2, 6, 12]),
        cube([220, 53], [-1, -6, 30], [2, 6, 12]),
      ],
      pos: [0, -12, -16],
    }, T, T, this.skin);
    this.body.inner.add(bodyG);
    // wings pivot at the shoulders (vanilla pivot (+-12, 5, 2) from the body origin)
    for (const side of [1, -1]) {
      const root = new THREE.Group();
      root.position.set(side * 12, 5, 2);
      root.rotation.order = 'ZYX';
      const bone = buildPart({
        cubes: [
          cube([112, 88], side > 0 ? [0, -4, -4] : [-56, -4, -4], [56, 8, 8], 0, side < 0),
          cube([-56, 88], side > 0 ? [0, 0, 2] : [-56, 0, 2], [56, 0, 56], 0, side < 0),
        ],
      }, T, T, this.skin);
      const tip = new THREE.Group();
      tip.position.set(side * 56, 0, 0);
      tip.rotation.order = 'ZYX';
      tip.add(buildPart({
        cubes: [
          cube([112, 136], side > 0 ? [0, -2, -2] : [-56, -2, -2], [56, 4, 4], 0, side < 0),
          cube([-56, 144], side > 0 ? [0, 0, 2] : [-56, 0, 2], [56, 0, 56], 0, side < 0),
        ],
      }, T, T, this.skin));
      root.add(bone, tip);
      bodyG.add(root);
      // pivots are in body-origin space: undo the body group's own offset
      root.position.y += 12; root.position.z += 16;
      this.wings.push({ root, tip, side });
    }

    // legs (vanilla DragonModel): thigh -> shin -> foot, pivots in body-origin space
    const leg = (x: number, y: number, z: number, rear: boolean): THREE.Group => {
      const up = rear ? cube([112, 104], [-8, -4, -8], [16, 32, 16]) : cube([112, 104], [-4, -4, -4], [8, 24, 8]);
      const root = buildPart({ cubes: [up], pos: [x, y + 12, z + 16] }, T, T, this.skin);
      root.rotation.order = 'ZYX';
      const shin = buildPart({
        cubes: [rear ? cube([196, 132], [-6, -2, 0], [12, 32, 12]) : cube([226, 138], [-3, -1, -3], [6, 24, 6])],
        pos: rear ? [0, 32, -4] : [0, 20, -1],
      }, T, T, this.skin);
      const foot = buildPart({
        cubes: [rear ? cube([112, 0], [-9, 0, -20], [18, 6, 24]) : cube([144, 104], [-4, 0, -12], [8, 4, 16])],
        pos: rear ? [0, 31, 4] : [0, 23, 0],
      }, T, T, this.skin);
      shin.add(foot); root.add(shin);
      bodyG.add(root);
      this.legs.push({ root, shin, rear });
      return root;
    };
    for (const side of [1, -1]) { leg(side * 12, 20, 2, false); leg(side * 16, 16, 42, true); }

    // head: skull, lips, jaw, nostrils, horns, glowing eyes
    const skull = buildPart({
      cubes: [
        cube([112, 30], [-8, -8, -10], [16, 16, 16]),
        cube([176, 44], [-6, -1, -24], [12, 5, 16]),
        cube([112, 0], [-5, -3, -22], [2, 2, 4]),
        cube([112, 0], [3, -3, -22], [2, 2, 4], 0, true),
        cube([0, 0], [-5, -12, -4], [2, 4, 6]),
        cube([0, 0], [3, -12, -4], [2, 4, 6], 0, true),
      ],
    }, T, T, this.skin);
    this.jaw = buildPart({ cubes: [cube([176, 65], [-6, 0, -16], [12, 4, 16])], pos: [0, 4, -8] }, T, T, this.skin);
    // vanilla DragonEyesLayer: the head re-drawn with the emissive eyes sheet
    const eyes = buildPart({
      cubes: [
        cube([112, 30], [-8, -8, -10], [16, 16, 16]),
        cube([176, 44], [-6, -1, -24], [12, 5, 16]),
      ],
    }, T, T, eye);
    skull.add(this.jaw);
    skull.position.z = 8;
    eyes.position.z = 8;
    this.head.inner.add(skull, eyes);

    const link = (): ReturnType<typeof holder> => {
      const h = holder();
      h.inner.add(buildPart({ cubes: [cube([192, 104], [-5, -5, -5], [10, 10, 10]), cube([48, 0], [-1, -9, -3], [2, 4, 6])] }, T, T, this.skin));
      return h;
    };
    for (let i = 0; i < 5; i++) { const l = link(); this.neck.push(l); this.root.add(l.wrap); }
    for (let i = 0; i < 12; i++) { const l = link(); this.tail.push(l); this.root.add(l.wrap); }
    this.root.add(this.body.wrap, this.head.wrap);
  }

  private static place(h: ReturnType<typeof holder>, l: ChainLink, scale = 1): void {
    h.wrap.position.set(l.x, l.y, l.z);
    h.wrap.rotation.set(l.pitch, l.yaw, 0);
    h.wrap.scale.setScalar(scale);
  }

  pose(d: EnderDragon, a: number): void {
    const yaw = d.pyaw + wrap(d.yaw - d.pyaw) * a;
    const pitch = d.ppitch + (d.pitch - d.ppitch) * a;
    const x = d.px + (d.x - d.px) * a, y = d.py + (d.y - d.py) * a, z = d.pz + (d.z - d.pz) * a;
    // the body origin is 16px ahead of the model centre, which sits on the hitbox centre
    DragonModel.place(this.body, { x, y: y + 1.5, z, yaw, pitch });
    const flap = d.pflap + (d.flap - d.pflap) * a;
    const w = Math.sin(flap) * 0.55 + (d.perched ? 0.25 : 0.1);
    for (const wg of this.wings) {
      wg.root.rotation.z = -wg.side * w;
      wg.root.rotation.y = wg.side * -0.15 * Math.cos(flap);
      wg.tip.rotation.z = -wg.side * (Math.sin(flap - 1.1) * 0.5 - 0.15);
    }
    // legs hang straight while perched, tuck up and back in flight
    const tuck = d.perched || d.phase === 'landing' ? 0 : 1;
    for (const l of this.legs) {
      l.root.rotation.x = l.rear ? 0.5 * tuck + 0.1 * Math.sin(flap) : -0.9 * tuck;
      l.shin.rotation.x = l.rear ? -0.9 * tuck : 1.4 * tuck;
    }
    const neck = d.neckChain(a), tail = d.tailChain(a);
    for (let i = 0; i < 5; i++) DragonModel.place(this.neck[i], neck[i], 1);
    for (let i = 0; i < 12; i++) DragonModel.place(this.tail[i], tail[i], 1 - i * 0.045);
    const h = d.head(a);
    DragonModel.place(this.head, h);
    this.jaw.rotation.x = d.mouth * 0.7 + (0.04 + Math.sin(flap * 0.5) * 0.02);
  }

  dispose(): void { for (const m of this.mats) m.dispose(); }
}

const wrap = (a: number): number => { a %= Math.PI * 2; if (a > Math.PI) a -= Math.PI * 2; else if (a < -Math.PI) a += Math.PI * 2; return a; };

export class DragonRenderer {
  readonly group = new THREE.Group();
  private readonly model: DragonModel;
  private readonly crystals = new Map<number, CrystalModel>();
  private readonly beams = new BeamPool();
  private readonly beamMat = new THREE.MeshBasicMaterial({
    color: 0xe9c8ff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
  });
  private readonly beamGeo = new THREE.CylinderGeometry(1, 1, 1, 12, 1, true);
  private readonly deathBeam = new THREE.Mesh(this.beamGeo, this.beamMat);
  private readonly cloudMat = new THREE.MeshBasicMaterial({
    color: 0xb04cf0, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
  });
  private readonly cloudGeo = new THREE.CircleGeometry(1, 20).rotateX(-Math.PI / 2);
  private readonly clouds = new Map<number, THREE.Mesh>();
  private readonly balls = new Map<number, { mesh: THREE.Mesh; mat: EntityMat }>();

  constructor(private readonly env: EnvUniforms) {
    this.model = new DragonModel(env);
    this.group.add(this.model.root, this.beams.group, this.deathBeam);
    this.deathBeam.visible = false;
    this.deathBeam.frustumCulled = false;
    this.model.root.visible = false;
  }

  update(fight: DragonFight, alpha: number, camera: THREE.Camera, tick: number): void {
    const t = tick + alpha;
    // --- crystals + beams
    this.beams.begin();
    const seen = new Set<number>();
    const d = fight.dragon;
    for (const c of fight.crystals) {
      seen.add(c.id);
      let m = this.crystals.get(c.id);
      if (!m) { m = new CrystalModel(this.env); this.crystals.set(c.id, m); this.group.add(m.root); }
      const bob = m.pose(c.age + alpha);
      m.root.position.set(c.x, c.y + 1.1 + bob * 0.5, c.z);
      setEffects([m.mat], 1, false, 0);
      if (c.beam && d && d.alive) {
        const hx = d.x, hy = d.y + 2, hz = d.z;
        this.beams.add(c.x, c.y + 1.5 + bob * 0.5, c.z, hx, hy, hz, t);
      }
    }
    for (const [id, m] of this.crystals) {
      if (seen.has(id)) continue;
      m.root.removeFromParent(); m.dispose(); this.crystals.delete(id);
    }
    this.beams.end();

    // --- dragon
    if (d && !d.removed) {
      this.model.root.visible = true;
      this.model.pose(d, alpha);
      setEffects(this.model.mats.slice(0, 1), 1, false, 0, { x: d.x, y: d.y + 2, z: d.z });
      this.model.mats[0].uniforms.uHurt.value = d.hurtTime > 0 ? 0.3 : 0;
      this.model.mats[0].uniforms.uFlash.value = d.dying ? Math.min(0.4, d.deathTime / DEATH_TICKS) * (0.5 + 0.5 * Math.sin(t * 0.8)) : 0;
      this.drawDeathBeam(d, alpha, t);
    } else {
      this.model.root.visible = false;
      this.deathBeam.visible = false;
    }

    // --- fireballs (purple-tinted charges)
    const live = new Set<number>();
    for (const f of fight.fireballs) {
      live.add(f.id);
      let b = this.balls.get(f.id);
      if (!b) {
        b = itemSpriteMesh(this.env, 'fire_charge', true);
        b.mat.uniforms.uTint.value.set(0.75, 0.45, 1);
        this.group.add(b.mesh);
        this.balls.set(f.id, b);
      }
      b.mesh.position.set(f.px + (f.x - f.px) * alpha, f.py + (f.y - f.py) * alpha, f.pz + (f.z - f.pz) * alpha);
      b.mesh.quaternion.copy(camera.quaternion);
      b.mesh.scale.setScalar(1.5);
    }
    for (const [id, b] of this.balls) {
      if (live.has(id)) continue;
      b.mesh.removeFromParent(); b.mat.dispose(); this.balls.delete(id);
    }

    // --- breath clouds (flat purple discs)
    const cl = new Set<number>();
    for (const c of fight.clouds) {
      cl.add(c.id);
      let m = this.clouds.get(c.id);
      if (!m) { m = new THREE.Mesh(this.cloudGeo, this.cloudMat); m.frustumCulled = false; this.group.add(m); this.clouds.set(c.id, m); }
      m.position.set(c.x, c.y + 0.08, c.z);
      m.scale.set(Math.max(0.1, c.radius), 1, Math.max(0.1, c.radius));
    }
    for (const [id, m] of this.clouds) {
      if (cl.has(id)) continue;
      m.removeFromParent(); this.clouds.delete(id);
    }
  }

  /** Rising white-violet column once the dragon starts to die (grows over the 200 ticks). */
  private drawDeathBeam(d: EnderDragon, a: number, t: number): void {
    if (!d.dying) { this.deathBeam.visible = false; return; }
    const k = Math.min(1, (d.deathTime + a) / DEATH_TICKS);
    this.deathBeam.visible = true;
    const h = 320;
    const r = (0.4 + k * 3.5) * (1 + Math.sin(t * 1.3) * 0.06);
    this.deathBeam.scale.set(r, h, r);
    this.deathBeam.position.set(d.x, d.y + 2 + h / 2, d.z);
    this.beamMat.opacity = 0.15 + 0.5 * k;
  }

  dispose(): void {
    this.model.dispose(); this.beams.dispose(); this.beamMat.dispose(); this.beamGeo.dispose();
    this.cloudMat.dispose(); this.cloudGeo.dispose();
    for (const m of this.crystals.values()) m.dispose();
    for (const b of this.balls.values()) b.mat.dispose();
  }
}
